import {Logger} from '@unidev-hub/logger';
import {ConfigManager} from '@unidev-hub/config';
import {DatabaseConnection} from '@unidev-hub/database';
import {DatabaseOptions} from '../../types';
import {closePostgresConnection, initializePostgres} from './postgres';
import path from 'path';

// Track the current database connection and type
let currentDbConnection: DatabaseConnection | null = null;
let currentDbType: string | null = null;

/**
 * Initialize database connection
 *
 * @param config Configuration manager
 * @param logger Logger instance
 * @param options Database options
 * @returns Database connection
 */
export async function initializeDatabase(
  config: ConfigManager,
  logger: Logger,
  options: DatabaseOptions = {}
): Promise<DatabaseConnection | null> {
  try {
    // Check if database is required
    const dbRequired = options.required !== undefined
      ? options.required
      : config.get<boolean>('database.required', true);

    // If database is not required, log it and return null
    if (!dbRequired) {
      logger.info('Database connection not required, skipping initialization');
      return null;
    }

    // Determine the database type
    const dbType = options.type || config.get<string>('database.type', 'postgres');

    logger.info(`Initializing ${dbType} database connection`);

    // Try to load schema from options or from default location if not provided
    if (!options.schema && !options.schemaPath) {
      try {
        // Get project root directory for proper path resolution
        const projectRoot = process.cwd();

        // Look for schema in standard locations within the project
        const possibleSchemaPaths = [
          path.join(projectRoot, 'src', 'schema'),
          path.join(projectRoot, 'src', 'database', 'schema'),
          path.join(projectRoot, 'dist', 'schema'),
          path.join(projectRoot, 'dist', 'database', 'schema'),
          path.join(projectRoot, 'schema')
        ];

        logger.debug(`Looking for schema in standard locations: ${possibleSchemaPaths.join(', ')}`);

        // Try each path until we find a schema
        for (const schemaPath of possibleSchemaPaths) {
          try {
            logger.debug(`Attempting to load schema from path: ${schemaPath}`);

            try {
              // First try a dynamic import (ESM)
              // Use explicit string casting to avoid TypeScript error
              const schema = await import(String(schemaPath));
              options.schema = schema;
              logger.debug(`Successfully loaded schema from ${schemaPath} with dynamic import`);
              break; // Exit the loop if successful
            } catch (importError) {
              // Fall back to require if dynamic import fails (CommonJS)
              try {
                // Use require only after checking it exists, and with a string path
                if (typeof require === 'function') {
                  options.schema = require(String(schemaPath));
                  logger.debug(`Successfully loaded schema from ${schemaPath} with require`);
                  break; // Exit the loop if successful
                } else {
                  logger.debug(`require() not available, skipping CommonJS import`);
                }
              } catch (requireError) {
                // Continue to the next path
                logger.debug(`Could not load schema from ${schemaPath}`, {
                  error: (requireError as Error).message
                });
              }
            }
          } catch (pathError) {
            // Skip this path and try the next one
            logger.info(`Error with schema path ${schemaPath}`, {
              error: (pathError as Error).message
            });
          }
        }

        // If we still don't have a schema, log a warning
        if (!options.schema) {
          logger.warn('Could not load schema from any standard path, Drizzle ORM will initialize without schema');
        }
      } catch (schemaError) {
        logger.warn('Error while trying to load schema', {
          error: (schemaError as Error).message
        });
      }
    }

    let dbConnection: DatabaseConnection;

    // Initialize the appropriate database
    switch (dbType) {
      case 'postgres':
        dbConnection = await initializePostgres(config, logger, options);
        break;

      default:
        throw new Error(`Unsupported database type: ${dbType}`);
    }

    // Store the current connection and type
    currentDbConnection = dbConnection;
    currentDbType = dbType;

    // Log successful connection with Drizzle info
    if (dbConnection.db) {
      logger.info(`${dbType} database connection established successfully with Drizzle ORM`);
    } else {
      logger.info(`${dbType} database connection established successfully`);
    }

    return dbConnection;
  } catch (error) {
    logger.error('Failed to initialize database connection', error as Error);
    throw error;
  }
}

/**
 * Close the current database connection
 *
 * @param logger Logger instance
 */
export async function closeDatabase(logger: Logger): Promise<void> {
  if (!currentDbConnection) {
    logger.warn('No database connection to close');
    return;
  }

  try {
    logger.info(`Closing ${currentDbType} database connection`);

    // Try different methods to close the connection based on the type
    switch (currentDbType) {
      case 'postgres':
        await closePostgresConnection(currentDbConnection, logger);
        break;

      default:
        // Check for disconnect or close method
        const conn = currentDbConnection as any;
        if (conn && typeof conn.disconnect === 'function') {
          await conn.disconnect();
        } else if (conn && typeof conn.close === 'function') {
          await conn.close();
        } else {
          logger.warn(`No close or disconnect method found for ${currentDbType}`);
        }
    }

    // Reset the current connection
    currentDbConnection = null;

    logger.info('Database connection closed successfully');
  } catch (error) {
    logger.error('Error closing database connection', error as Error);
    throw error;
  }
}

/**
 * Get the current database connection
 * This is useful for direct access to the Drizzle ORM instance in other parts of the application
 *
 * @returns The current database connection or null if not initialized
 */
export function getCurrentDatabaseConnection(): DatabaseConnection | null {
  return currentDbConnection;
}

// Export the database functions
export { initializePostgres, closePostgresConnection };
